import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useBasket } from '../hooks/useBasket';

export default function OrderHistory() {
  const { items: basketItems, addItems, startEditOrder } = useBasket();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');
  const [addedSuccess, setAddedSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const catalogRef = useRef(null);

  useEffect(() => {
    api.getOrders({ pagesize: 50 })
      .then(res => setOrders(res.orders || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Lazy-load the catalog once so reused orders pick up current prices.
  async function ensureCatalog() {
    if (catalogRef.current) return catalogRef.current;
    const res = await api.getCatalog();
    // Order lines carry the SOS item id; the catalog's own `id` is a different
    // number (and a few collide with other products' SOS ids), so match on sosId.
    const map = new Map();
    (res.products || []).forEach(p => { if (p.sosId != null) map.set(String(p.sosId), p); });
    catalogRef.current = map;
    return map;
  }

  async function reuseOrder(order) {
    setBusy(true);
    setError('');
    try {
      const map = await ensureCatalog();
      // Only what's still sold online comes back, at today's price. Old
      // "Discount Given" lines (negative) aren't products: today's specials
      // apply on their own at checkout.
      const wanted = (order.lines || []).filter(l => l.itemId && l.quantity > 0 && !(Number(l.price) < 0));
      const gone = wanted.filter(l => !map.has(String(l.itemId)));
      const items = wanted
        .filter(l => map.has(String(l.itemId)))
        .map(l => {
          const prod = map.get(String(l.itemId));
          return {
            itemId: l.itemId,
            sosId: l.itemId,
            name: prod?.name || l.itemName || 'Unknown item',
            // Carry the long-form detail (pack size, etc.) onto the SOS line so
            // reorders match a fresh order; prefer today's catalog description,
            // fall back to the description from the original order line.
            description: prod?.description || l.description || '',
            sku: prod?.sku || l.sku || '',
            quantity: l.quantity,
            price: prod.price || 0,
          };
        });
      const goneNote = gone.length
        ? ` ${gone.length} item${gone.length !== 1 ? 's are' : ' is'} no longer available online (${gone.slice(0, 3).map(l => l.itemName || 'item').join(', ')}${gone.length > 3 ? '…' : ''}). Call us to order ${gone.length !== 1 ? 'them' : 'it'}.`
        : '';
      if (items.length === 0) {
        setError(`Nothing from ${order.number || 'this order'} can be reordered online.${goneNote}`);
        return;
      }
      addItems(items);
      setAddedSuccess(`${items.length} item${items.length !== 1 ? 's' : ''} from ${order.number || 'order'} added to basket at current prices.${goneNote}`);
      setTimeout(() => setAddedSuccess(''), 6000);
    } catch (e) {
      setError(e.message || 'Could not reuse this order');
    } finally {
      setBusy(false);
    }
  }

  function editOrder(order) {
    if (basketItems.length > 0) {
      const ok = window.confirm('Editing this order will replace your current basket contents. Continue?');
      if (!ok) return;
    }
    // Old "Discount Given" lines (negative) aren't products; the order is
    // repriced with today's specials when it's saved.
    const lines = (order.lines || [])
      .filter(l => l.itemId && l.quantity > 0 && !(Number(l.price) < 0))
      .map(l => ({
        itemId: l.itemId,
        sosId: l.itemId,
        name: l.itemName || 'Unknown item',
        sku: l.sku || '',
        quantity: l.quantity,
        price: l.price || 0,
      }));
    startEditOrder(order, lines);
    window.location.hash = '#basket';
  }

  // Customer-facing badge. The only reliable state SOS gives us is the
  // `closed` flag (set once an order is closed or invoiced) — there is no
  // top-level status string, so we drive the badge off `closed`.
  // "Received" is friendlier than the internal "Open" for a placed order.
  // The order.status branches below are a forward-compat fallback in case
  // SOS ever starts populating a real status string.
  const statusStyle = (order) => {
    if (order.closed) return { label: 'Closed', fg: '#505862', bg: '#eef0f2' };
    const st = (order.status || 'open').toLowerCase();
    if (['shipped','in-transit','in transit'].includes(st))        return { label: 'Shipped',   fg: '#1e6f3a', bg: '#e6f4ea' };
    if (['cancelled','canceled','voided'].includes(st))            return { label: 'Cancelled', fg: '#a82818', bg: '#fde9e6' };
    if (['hold','on hold'].includes(st))                           return { label: 'On Hold',   fg: '#8a5a00', bg: '#fff4d1' };
    // Default for an open (not-yet-closed) order.
    return { label: 'Received', fg: '#0f5c99', bg: '#e2effb' };
  };

  if (loading) return <div style={{color:'rgba(0,0,0,0.4)',padding:'3rem',textAlign:'center'}}>Loading order history...</div>;

  return (
    <div>
      <div data-page-header style={styles.header}>
        <h1 style={styles.title}>Order History</h1>
        <p style={styles.subtitle}>{orders.length} orders · Most recent first</p>
      </div>

      {addedSuccess && (
        <div style={styles.successBanner}>
          {addedSuccess} <a href="#basket" style={{color:'#27ae60', fontWeight:600}}>View Basket</a>
          <button style={styles.dismissBtn} onClick={() => setAddedSuccess('')}>✕</button>
        </div>
      )}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {orders.length === 0 && !loading && (
        <div style={styles.empty}>No orders found. Place your first order using the <a href="#catalog" style={{color:'#A50B18'}}>Product Catalog</a>.</div>
      )}

      <div style={styles.list}>
        {orders.map(order => (
          <div key={order.id} style={styles.card}>
            <div style={styles.cardHeader} onClick={() => setExpanded(expanded === order.id ? null : order.id)}>
              <div style={styles.cardLeft}>
                <div style={styles.orderNum}>{order.number || `Order #${order.id}`}</div>
                <div style={styles.orderDate}>
                  {order.date ? new Date(order.date).toLocaleDateString('en-US', {weekday:'short', month:'short', day:'numeric', year:'numeric'}) : '—'}
                </div>
              </div>
              <div style={styles.cardRight}>
                {(() => {
                  const st = statusStyle(order);
                  return (
                    <span style={{...styles.statusBadge, color: st.fg, background: st.bg}}>
                      {st.label}
                    </span>
                  );
                })()}
                <span style={styles.orderTotal}>${(order.total || 0).toFixed(2)}</span>
                <span style={styles.lineCount}>{order.lines?.length || 0} item{order.lines?.length !== 1 ? 's' : ''}</span>
                <span style={styles.chevron}>{expanded === order.id ? '▲' : '▼'}</span>
              </div>
            </div>

            {expanded === order.id && (
              <div style={styles.cardBody}>
                {order.customerPo && <div style={styles.poRow}>PO# <strong>{order.customerPo}</strong></div>}
                {order.shipDate && <div style={styles.poRow}>Requested: <strong>{new Date(order.shipDate).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</strong></div>}

                <table style={styles.linesTable}>
                  <thead>
                    <tr>
                      <th style={linesTh('left')}>Product</th>
                      <th style={linesTh('center')}>Qty</th>
                      <th style={linesTh('right')}>Unit Price</th>
                      <th style={linesTh('right')}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(order.lines || []).map((line, i) => (
                      <tr key={i}>
                        <td style={linesTd('left')}>
                          <div style={{fontWeight:500}}>{line.itemName || '—'}</div>
                          {line.description && line.description !== line.itemName && (
                            <div style={{fontSize:'0.8rem',color:'rgba(0,0,0,0.55)',lineHeight:1.35,marginTop:2}}>{line.description}</div>
                          )}
                          {line.sku && <div style={{fontSize:'0.72rem',color:'rgba(0,0,0,0.35)',fontFamily:'monospace',marginTop:2}}>{line.sku}</div>}
                        </td>
                        <td style={{...linesTd('center'),fontFamily:'monospace'}}>{line.quantity}</td>
                        <td style={{...linesTd('right'),fontFamily:'monospace'}}>{line.price ? `$${line.price.toFixed(2)}` : '—'}</td>
                        <td style={{...linesTd('right'),fontFamily:'monospace',color:'#A50B18'}}>{line.total ? `$${line.total.toFixed(2)}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={styles.cardActions}>
                  {!order.closed && (
                    <button
                      style={styles.editOrderBtn}
                      onClick={() => editOrder(order)}
                      disabled={busy}
                    >
                      ✏️ Edit Order
                    </button>
                  )}
                  <button
                    style={{ ...styles.addToBasketBtn, ...(!order.closed ? styles.addToBasketBtnDisabled : {}) }}
                    onClick={() => reuseOrder(order)}
                    disabled={busy || !order.closed}
                    title={!order.closed ? 'This order is still open — edit it instead of reusing it.' : undefined}
                  >
                    🛒 Reuse This Order
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const linesTh = (align) => ({ padding:'0.6rem 0.75rem', textAlign:align, fontSize:'0.7rem', color:'rgba(0,0,0,0.4)', letterSpacing:'0.08em', textTransform:'uppercase', borderBottom:'1px solid #e0e0e0' });
const linesTd = (align) => ({ padding:'0.7rem 0.75rem', textAlign:align, fontSize:'0.875rem', borderBottom:'1px solid #f0f0f0' });

const styles = {
  header: { marginBottom:'1.5rem' },
  title: { fontSize:'1.5rem', fontWeight:700, color:'#333', margin:0 },
  subtitle: { fontSize:'0.82rem', color:'rgba(0,0,0,0.4)', margin:'0.3rem 0 0' },
  successBanner: { background:'rgba(39,174,96,0.08)', border:'1px solid rgba(39,174,96,0.25)', borderRadius:8, padding:'1rem 1.25rem', color:'#27ae60', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' },
  dismissBtn: { background:'none', border:'none', color:'inherit', cursor:'pointer', fontSize:'1rem' },
  errorBanner: { background:'rgba(231,76,60,0.08)', border:'1px solid rgba(231,76,60,0.25)', borderRadius:8, padding:'1rem 1.25rem', color:'#c0392b', marginBottom:'1rem' },
  empty: { textAlign:'center', color:'rgba(0,0,0,0.35)', padding:'4rem 0' },
  list: { display:'flex', flexDirection:'column', gap:'0.75rem' },
  card: { background:'#fff', border:'1px solid #e0e0e0', borderRadius:10, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' },
  cardHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1rem 1.25rem', cursor:'pointer', flexWrap:'wrap', gap:'0.5rem' },
  cardLeft: { display:'flex', flexDirection:'column', gap:2 },
  orderNum: { fontSize:'0.95rem', fontWeight:600, color:'#333' },
  orderDate: { fontSize:'0.8rem', color:'rgba(0,0,0,0.4)' },
  cardRight: { display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap' },
  statusBadge: { fontSize:'0.72rem', fontWeight:600, borderRadius:12, padding:'0.22rem 0.7rem', letterSpacing:'0.02em' },
  orderTotal: { fontFamily:'monospace', color:'#A50B18', fontWeight:600 },
  lineCount: { fontSize:'0.8rem', color:'rgba(0,0,0,0.4)' },
  chevron: { color:'rgba(0,0,0,0.3)', fontSize:'0.7rem' },
  cardBody: { borderTop:'1px solid #e0e0e0', padding:'1.25rem' },
  poRow: { fontSize:'0.82rem', color:'rgba(0,0,0,0.5)', marginBottom:'0.75rem' },
  linesTable: { width:'100%', borderCollapse:'collapse', marginBottom:'1rem' },
  cardActions: { display:'flex', justifyContent:'flex-end', gap:'0.6rem' },
  addToBasketBtn: { background:'#A50B18', color:'#fff', border:'none', borderRadius:6, padding:'0.6rem 1.25rem', fontWeight:600, cursor:'pointer', fontSize:'0.85rem' },
  addToBasketBtnDisabled: { background:'#d9b3b6', cursor:'not-allowed' },
  editOrderBtn: { background:'#fff', color:'#A50B18', border:'1px solid #A50B18', borderRadius:6, padding:'0.6rem 1.25rem', fontWeight:600, cursor:'pointer', fontSize:'0.85rem' },
};
